import { usePortalState } from "./states";
import { apiFetcher } from "./utils.client";

/**
 * Portal API utilities for booking flow
 * Uses selected tenant context instead of subdomain-based tenant extraction
 */

export interface PortalApiOptions {
	method?: string;
	body?: string | FormData;
	headers?: Record<string, string>;
}

/**
 * Portal API fetcher that automatically includes tenant context
 * @param url - API endpoint URL
 * @param options - Request options
 * @returns Promise<Response>
 */
export async function portalApiFetcher(
	url: string,
	options: PortalApiOptions = {},
): Promise<Response> {
	const { selectedTenant, isPortalFlow } = usePortalState.getState();

	// For portal flow, use selected tenant ID
	// For direct flow, fall back to normal apiFetcher (subdomain-based)
	if (isPortalFlow()) {
		const tenantId = selectedTenant?.tenant_id;
		if (!tenantId) {
			throw new Error("No tenant selected for portal booking flow");
		}

		// Add tenant ID to headers
		const headers = {
			...options.headers,
			"x-tenant-id": tenantId,
		};

		return apiFetcher({
			path: url,
			opts: {
				...options,
				headers,
			},
		});
	}

	// Fall back to normal API fetcher for direct flow
	return apiFetcher({
		path: url,
		opts: options,
	});
}

/**
 * Portal-specific API endpoints
 */
export const portalApi = {
	// Booking flow endpoints with portal context
	async getPolis() {
		return portalApiFetcher("/api/units");
	},

	async getDoctors(unitId: string) {
		return portalApiFetcher(`/units/${unitId}/doctors`);
	},

	async getDoctorSchedule(doctorId: string, date: string) {
		return portalApiFetcher(`/api/doctors/${doctorId}/schedules?date=${date}`);
	},

	async getPaymentMethods() {
		return portalApiFetcher("/api/payment-methods");
	},

	async createBooking(bookingData: Record<string, unknown>) {
		return portalApiFetcher("/api/bookings", {
			method: "POST",
			body: JSON.stringify(bookingData),
			headers: {
				"Content-Type": "application/json",
			},
		});
	},

	async getPatientData() {
		return portalApiFetcher("/api/patient");
	},

	async getBookingHistory() {
		return portalApiFetcher("/api/bookings/history");
	},
};

/**
 * Helper to check if we're in portal flow mode
 */
export function usePortalFlowCheck() {
	const { isPortalFlow, canStartBooking, selectedTenant } = usePortalState();

	return {
		isPortalFlow: isPortalFlow(),
		canStartBooking: canStartBooking(),
		selectedTenant,
		hasValidTenant: selectedTenant !== null,
	};
}

/**
 * Portal navigation helpers
 */
export const portalNavigation = {
	// Portal booking flow routes
	toPolis: () => "/portal/unit",
	toDoctors: (unitId: string) => `/portal/unit/${unitId}`,
	toBooking: () => "/portal/booking",
	toConfirmation: () => "/portal/confirmation",
	toHistory: () => "/portal/history",

	// Check if current route is portal flow
	isPortalRoute: (pathname: string) => pathname.startsWith("/portal/"),
};
